/**
 * Shared visual contract for rich HTML edit and view.
 * Web edit (Tiptap), native edit (10tap WKWebView), web view (real DOM),
 * and native view (`RICH_HTML_TAGS_STYLES`) must use these tokens.
 *
 * Body/placeholder match FormInput (`#333` / `#999` / 14px / 20px line).
 */

import { colors } from '../../theme/colors';
import { fontSize, lineHeight } from '../../theme/typography';

export const RICH_HTML_BODY_COLOR = colors.textPrimary;
export const RICH_HTML_PLACEHOLDER_COLOR = colors.inputPlaceholder;
export const RICH_HTML_BODY_FONT_SIZE = fontSize.formControl;
export const RICH_HTML_BODY_LINE_HEIGHT = lineHeight.formControl;

/**
 * Extra gap below a block on top of the 20px line box. Tighter than the
 * legacy `0.5em`, but non-zero so a hard return still reads as a new block
 * rather than a wrapped line.
 */
export const RICH_HTML_BLOCK_SPACING = 4;
export const RICH_HTML_LIST_ITEM_SPACING = 4;
/** Headings still need air so they do not collide with the next block. */
export const RICH_HTML_HEADING_SPACING = 8;

export const RICH_HTML_IMG_MAX_WIDTH = 240;
export const RICH_HTML_IMG_MAX_HEIGHT = 160;
export const RICH_HTML_LINK_COLOR = '#2563EB';
export const RICH_HTML_MARK_BG = '#FEF08A';
export const RICH_HTML_TABLE_BORDER = '#D1D5DB';
export const RICH_HTML_TABLE_HEADER_BG = '#F3F4F6';

/**
 * A literal U+00A0, not the `\00a0` CSS escape. 10tap embeds each bridge's CSS
 * in a JS template literal (`getStyleSheetCSS`), where `\0` followed by a digit
 * is an illegal octal escape — one such sequence is a SyntaxError that throws
 * away *every* bridge stylesheet, leaving the native editor on UA defaults.
 */
const RICH_HTML_EMPTY_PARAGRAPH_CONTENT = '\u00a0';

/** CSS for one scoped root (`.ss-rich-html-view`, `.ProseMirror`, …). */
export function richHtmlScopedCss(scope: string): string {
  return `
${scope} {
  color: ${RICH_HTML_BODY_COLOR};
  font-size: ${RICH_HTML_BODY_FONT_SIZE}px;
  line-height: ${RICH_HTML_BODY_LINE_HEIGHT}px;
}
${scope} p {
  margin: 0 0 ${RICH_HTML_BLOCK_SPACING}px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}
${scope} p:empty:not(.is-editor-empty)::before {
  content: '${RICH_HTML_EMPTY_PARAGRAPH_CONTENT}';
}
${scope} h1 {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 ${RICH_HTML_HEADING_SPACING}px;
}
${scope} h2 {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 ${RICH_HTML_HEADING_SPACING}px;
}
${scope} h3 {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 ${RICH_HTML_HEADING_SPACING}px;
}
${scope} a {
  color: ${RICH_HTML_LINK_COLOR};
  text-decoration: underline;
}
${scope} mark {
  background-color: ${RICH_HTML_MARK_BG};
  padding: 0 2px;
  border-radius: 2px;
}
${scope} img {
  max-width: ${RICH_HTML_IMG_MAX_WIDTH}px;
  max-height: ${RICH_HTML_IMG_MAX_HEIGHT}px;
  width: auto;
  height: auto;
  display: inline-block;
  vertical-align: top;
  object-fit: contain;
}
${scope} ul,
${scope} ol {
  margin: 0 0 ${RICH_HTML_BLOCK_SPACING}px;
  padding-left: 1.5em;
}
${scope} li {
  margin: 0 0 ${RICH_HTML_LIST_ITEM_SPACING}px;
}
${scope} li > p,
${scope} li > div {
  display: inline;
  margin: 0;
}
${scope} table {
  border-collapse: collapse;
  margin: 8px 0;
  width: max-content;
  max-width: none;
  table-layout: auto;
  word-break: normal;
  overflow-wrap: normal;
}
${scope} td,
${scope} th {
  border: none;
  min-width: 80px;
  padding: 6px 8px;
  vertical-align: top;
  word-break: normal;
  overflow-wrap: normal;
}
${scope} table[data-ss-grid] td,
${scope} table[data-ss-grid] th {
  border: 1px solid #D1D5DB;
}
${scope} td p,
${scope} th p {
  margin: 0;
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
}
${scope} th {
  background: ${RICH_HTML_TABLE_HEADER_BG};
  font-weight: 700;
}
${scope} pre {
  background: #f3f4f6;
  color: #111827;
  border-radius: 4px;
  padding: 8px;
  font-family: monospace;
  white-space: pre-wrap;
}
${scope} code {
  font-family: monospace;
  background: #F3F4F6;
  border-radius: 3px;
  padding: 1px 4px;
}
${scope} blockquote {
  border-left: 3px solid #9CA3AF;
  padding-left: 10px;
  margin: 0 0 ${RICH_HTML_BLOCK_SPACING}px;
}
`;
}
